import { useMemo } from "react";
import { API_URLS } from "src/api/api-urls";
import useApiServices from "src/api/use-api-services";
import { filtersDefaultValues } from "src/constants/filters-default-values";
import {
  TwitterApiDto,
  TwitterDtoType,
  TwitterInsightRaw,
  TwitterWordContent,
} from "src/types/platforms/twitter";
import { DashboardType } from "src/types/types";
import { useQueryParams } from "use-query-params";

const normalizeTwitterInsights = (insights?: TwitterInsightRaw[]) =>
  (insights || []).map((insight) => ({
    title: insight.title,
    value: {
      "Tweet Date": insight.tweet_date,
      "Tweet Text": insight.tweet_text,
      "Tweet Sentiment": insight.tweet_sentiment,
      "Author Name": insight.author_name,
      "Author Followers": insight.author_followers,
      "Tweet Likes": insight.tweet_likes,
      "Tweet Replies": insight.tweet_replies,
      "Tweet Retweets": insight.tweet_retweets,
      "Tweet Views": insight.tweet_views,
      "Tweet Link": insight.tweet_link || insight.tweet_url,
    },
  }));

const normalizeWords = (
  fallbackTitle: string,
  words?: TwitterWordContent[] | { title: string; content: TwitterWordContent[] }
) =>
  Array.isArray(words)
    ? {
        title: fallbackTitle,
        content: words,
      }
    : {
        title: words?.title || fallbackTitle,
        content: words?.content || [],
      };

const useTwitterDashboardView = ({ type }: { type: DashboardType }) => {
  const { useGetListApi } = useApiServices();
  const [query] = useQueryParams(filtersDefaultValues);

  const {
    data,
    isLoading: isDataLoading,
    error,
  } = useGetListApi<TwitterApiDto>({
    url: API_URLS[type].TWITTER.ANALYSIS,
    parameters: {
      keyword: query.keyword,
      sentiment: query.sentiment,
      language: query.language,
      start_date: query.start_date,
      end_date: query.end_date,
      author_verified: query.author_verified,
    },
  });

  const platformData = useMemo<TwitterDtoType | undefined>(() => {
    if (!data) return undefined;

    const analysis = (data.analysis || (data as any).analysis || data) ?? {};

    const insightsDetails =
      analysis.insightsDetails ??
      (analysis.insights ? normalizeTwitterInsights(analysis.insights) : []);

    const top5Words = normalizeWords("Top 5 Words", analysis.top5Words || analysis.top_5_words);

    const wordCloudChart = normalizeWords(
      "Word Cloud",
      analysis.wordCloudChart || analysis.word_cloud
    );

    const pieChart =
      analysis.pieChart ||
      (analysis.sentiment_distribution && {
        title: "Sentiment Distribution",
        content: analysis.sentiment_distribution.map((item: any) => ({
          label: item.label,
          value: item.value,
        })),
      });

    const areaChartByDate = analysis.areaChartByDate ||
      (analysis.tweets_by_date && {
        title: "Tweets by Date",
        content: {
          xAxis: analysis.tweets_by_date.map((item: any) => item.date),
          yAxis: analysis.tweets_by_date.map((item: any) => item.count),
        },
      }) || {
        title: "Tweets by Date",
        content: { xAxis: [], yAxis: [] },
      };

    const areaChartByKeyword =
      analysis.areaChartByKeyword ||
      (analysis.tweets_by_keyword_date && {
        title: "Tweets by Keyword and Date",
        content: analysis.tweets_by_keyword_date,
      });

    return {
      ...(data.analysis ? data : { ...data, analysis }),
      analysis: {
        cards: analysis.cards || [],
        insightsDetails: insightsDetails || [],
        top5Words,
        wordCloudChart,
        pieChart: pieChart || { title: "Sentiment", content: [] },
        areaChartByDate,
        areaChartByKeyword,
      },
    };
  }, [data]);

  return {
    platformData,
    isDataLoading,
    error,
  };
};

export default useTwitterDashboardView;
